import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Paper,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import useEditData from "@/hooks/editHooks/useEditData";
import useGetMetadata from "@/hooks/useGetMetaData";
import { getSingleSelectStyles, getMultiSelectStyles } from "@/lib/styles";
import { useTheme, alpha } from "@mui/material/styles";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const animatedComponents = makeAnimated();

const EditUser = ({ open, onClose, userDetails, setEditingUser }) => {
  const { t } = useTranslation();
  const { isLoading, SitesList, UsersList, Departments, userGroupsList } =
    useGetMetadata();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const theme = useTheme();
  const { mutate: editUser, isLoading: editIsLoading } = useEditData(
    "/api/v1/org-admin/users",
    ["users"]
  );

  const singleSelectStyles = getSingleSelectStyles(theme);
  const multiSelectStyles = getMultiSelectStyles(theme);

  const userType = [
    { value: "ORG_ADMIN", label: t("editUser.userTypeOrgAdmin") },
    { value: "ORG_USER", label: t("editUser.userTypeOrgUser") },
  ];

  const visibilityTypes = [
    { value: "ALL", label: t("editUser.visibilityAll") },
    { value: "SITE_BASED", label: t("editUser.visibilitySiteBased") },
    {
      value: "INVOLVEMENT_BASED",
      label: t("editUser.visibilityInvolvementBased"),
    },
  ];

  const DEFAULTDATA = {
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    user_type: null,
    manager_id: null,
    department_id: null,
    user_groups: [],
    site_ids: [],
    work_permit_visibility: null,
    observation_visibility: null,
  };

  const [userData, setUserData] = useState(DEFAULTDATA);
  const [formValid, setFormValid] = useState(false);
  const [first_nameFocused, setFirst_nameFocused] = useState(false);
  const [last_nameFocused, setLast_nameFocused] = useState(false);
  const [usernameFocused, setUsernameFocused] = useState(false);
  const [user_typeFocused, setUser_typeFocused] = useState(false);
  const [userGroupsFocused, setUserGroupsFocused] = useState(false);
  const [visibilitySettingsFocused, setVisibilitySettingsFocused] =
    useState(false);

  useEffect(() => {
    if (userDetails) {
      const workPermitSetting = userDetails.document_visibility_infos?.find(
        (info) => info.document_type === "WORK_PERMIT"
      );
      const observationSetting = userDetails.document_visibility_infos?.find(
        (info) => info.document_type === "OBSERVATION"
      );

      setUserData({
        first_name: userDetails.first_name || "",
        last_name: userDetails.last_name || "",
        username: userDetails.username || "",
        email: userDetails.email || "",
        user_type: userDetails.user_type
          ? {
              value: userDetails.user_type,
              label: t(`editUser.userType${userDetails.user_type}`),
            }
          : null,
        manager_id: userDetails.manager
          ? {
              value: userDetails.manager.id,
              label: `${userDetails.manager.first_name} ${userDetails.manager.last_name}`,
            }
          : null,
        department_id: userDetails.department
          ? {
              value: userDetails.department.id,
              label: userDetails.department.name,
            }
          : null,
        user_groups: userDetails.user_groups
          ? userDetails.user_groups.map((group) => ({
              value: group.id,
              label: group.name,
            }))
          : [],
        site_ids: userDetails.sites
          ? userDetails.sites.map((site) => ({
              value: site.id,
              label: site.name,
            }))
          : [],
        work_permit_visibility: workPermitSetting
          ? {
              value: workPermitSetting.visibility_type,
              label: t(
                `editUser.visibility${workPermitSetting.visibility_type.replace("_", "")}`
              ),
            }
          : null,
        observation_visibility: observationSetting
          ? {
              value: observationSetting.visibility_type,
              label: t(
                `editUser.visibility${observationSetting.visibility_type.replace("_", "")}`
              ),
            }
          : null,
      });
    }
  }, [userDetails, t]);

  const checkFormValidity = useCallback(() => {
    const isValid =
      userData.first_name &&
      userData.last_name &&
      userData.username &&
      userData.user_type &&
      userData.user_groups.length > 0 &&
      userData.work_permit_visibility &&
      userData.observation_visibility;
    setFormValid(isValid);
  }, [userData]);

  useEffect(() => {
    checkFormValidity();
  }, [userData, checkFormValidity]);

  const handleChange = (e, field) => {
    setUserData((prevState) => ({
      ...prevState,
      [field]: e.target.value,
    }));
  };

  const handleSingleSelect = (name) => (e) => {
    setUserData((prevState) => ({
      ...prevState,
      [name]: e ? { value: e.value, label: e.label } : null,
    }));
  };

  const handleMultiSelect = (name) => (e) => {
    setUserData((prevState) => ({
      ...prevState,
      [name]: e.map((x) => ({ value: x.value, label: x.label })),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formValid) return;

    const sentData = {
      user_id: userDetails.id,
      username: userData.username,
      first_name: userData.first_name,
      email: userData.email,
      last_name: userData.last_name,
      user_type: userData.user_type?.value,
      user_groups: userData.user_groups.map((group) => group.value),
      site_ids: userData.site_ids.map((site) => site.value),
      manager_id: userData.manager_id?.value,
      department_id: userData.department_id?.value,
      document_visibility_settings: [
        userData.work_permit_visibility && {
          document_visibility_setting_id:
            userDetails.document_visibility_infos?.find(
              (info) => info.document_type === "WORK_PERMIT"
            )?.id,
          visibility_type: userData.work_permit_visibility.value,
        },
        userData.observation_visibility && {
          document_visibility_setting_id:
            userDetails.document_visibility_infos?.find(
              (info) => info.document_type === "OBSERVATION"
            )?.id,
          visibility_type: userData.observation_visibility.value,
        },
      ].filter(Boolean),
    };

    editUser(sentData, {
      onSuccess: () => {
        setToast({
          open: true,
          message: t("editUser.successToast"),
          severity: "success",
        });
        setEditingUser(null);
        onClose();
      },
      onError: (err) => {
        handleApiError(err);
      },
    });
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        borderRadius={2}
      >
        <CircularProgress size={48} aria-label={t("editUser.loadingAria")} />
        <Typography variant="body1" ml={2} color="text.secondary">
          {t("editUser.loadingText")}
        </Typography>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          maxHeight: "90vh",
          boxShadow: theme.shadows[24],
        },
      }}
      aria-labelledby="edit-user-dialog-title"
    >
      <DialogTitle
        id="edit-user-dialog-title"
        component="div"
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: theme.palette.primary.contrastText,
          position: "relative",
          py: 3,
          px: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <EditIcon sx={{ fontSize: 32 }} />
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            {t("editUser.title")}
          </Typography>
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            opacity: 0.9,
            fontWeight: 400,
            mt: 1,
          }}
        >
          {t("editUser.subtitle")} {userDetails?.username}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
            },
          }}
          aria-label={t("editUser.closeDialogAria")}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflowY: "auto" }}>
        <Box sx={{ p: 4 }}>
          <form onSubmit={handleSubmit} aria-label={t("editUser.formAria")}>
            <Stack spacing={4}>
              {/* Personal Information Section */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      mr: 2,
                    }}
                  >
                    <PersonIcon sx={{ color: theme.palette.primary.main }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t("editUser.personalInfoTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("editUser.personalInfoDescription")}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label={t("editUser.firstNameLabel")}
                      fullWidth
                      value={userData.first_name}
                      onChange={(e) => handleChange(e, "first_name")}
                      onBlur={() => setFirst_nameFocused(true)}
                      error={first_nameFocused && !userData.first_name}
                      helperText={
                        first_nameFocused && !userData.first_name
                          ? t("editUser.firstNameError")
                          : ""
                      }
                      aria-label={t("editUser.firstNameAria")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label={t("editUser.lastNameLabel")}
                      fullWidth
                      value={userData.last_name}
                      onChange={(e) => handleChange(e, "last_name")}
                      onBlur={() => setLast_nameFocused(true)}
                      error={last_nameFocused && !userData.last_name}
                      helperText={
                        last_nameFocused && !userData.last_name
                          ? t("editUser.lastNameError")
                          : ""
                      }
                      aria-label={t("editUser.lastNameAria")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label={t("editUser.emailLabel")}
                      fullWidth
                      type="email"
                      value={userData.email}
                      onChange={(e) => handleChange(e, "email")}
                      aria-label={t("editUser.emailAria")}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mr: 1,
                              }}
                            >
                              <EmailIcon color="action" />
                            </Box>
                          ),
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label={t("editUser.usernameLabel")}
                      fullWidth
                      value={userData.username}
                      onChange={(e) => handleChange(e, "username")}
                      onBlur={() => setUsernameFocused(true)}
                      error={usernameFocused && !userData.username}
                      helperText={
                        usernameFocused && !userData.username
                          ? t("editUser.usernameError")
                          : ""
                      }
                      aria-label={t("editUser.usernameAria")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Organization Information Section */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      mr: 2,
                    }}
                  >
                    <BusinessIcon sx={{ color: theme.palette.info.main }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t("editUser.orgInfoTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("editUser.orgInfoDescription")}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {t("editUser.departmentLabel")}
                    </Typography>
                    <Select
                      components={animatedComponents}
                      options={Departments}
                      value={userData.department_id}
                      onChange={handleSingleSelect("department_id")}
                      placeholder={t("editUser.departmentPlaceholder")}
                      styles={{
                        ...singleSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      aria-label={t("editUser.departmentAria")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {t("editUser.userTypeLabel")} *
                    </Typography>
                    <Select
                      components={animatedComponents}
                      styles={{
                        ...singleSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      options={userType}
                      value={userData.user_type}
                      onChange={handleSingleSelect("user_type")}
                      placeholder={t("editUser.userTypePlaceholder")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      onBlur={() => setUser_typeFocused(true)}
                      aria-label={t("editUser.userTypeAria")}
                    />
                    {user_typeFocused && !userData.user_type && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {t("editUser.userTypeError")}
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {t("editUser.managerLabel")}
                    </Typography>
                    <Select
                      components={animatedComponents}
                      styles={{
                        ...singleSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      options={UsersList}
                      value={userData.manager_id}
                      onChange={handleSingleSelect("manager_id")}
                      placeholder={t("editUser.managerPlaceholder")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      aria-label={t("editUser.managerAria")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {t("editUser.userGroupsLabel")} *
                    </Typography>
                    <Select
                      components={animatedComponents}
                      styles={{
                        ...multiSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      isMulti
                      closeMenuOnSelect={false}
                      options={userGroupsList}
                      value={userData.user_groups}
                      onChange={handleMultiSelect("user_groups")}
                      placeholder={t("editUser.userGroupsPlaceholder")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      onBlur={() => setUserGroupsFocused(true)}
                      aria-label={t("editUser.userGroupsAria")}
                    />
                    {userGroupsFocused && userData.user_groups.length === 0 && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {t("editUser.userGroupsError")}
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {t("editUser.sitesLabel")}
                    </Typography>
                    <Select
                      components={animatedComponents}
                      styles={{
                        ...multiSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      isMulti
                      closeMenuOnSelect={false}
                      options={SitesList}
                      value={userData.site_ids}
                      onChange={handleMultiSelect("site_ids")}
                      placeholder={t("editUser.sitesPlaceholder")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      aria-label={t("editUser.sitesAria")}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Permissions Section */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      mr: 2,
                    }}
                  >
                    <SecurityIcon sx={{ color: theme.palette.success.main }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t("editUser.permissionsTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("editUser.permissionsDescription")}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {t("editUser.workPermitVisibilityLabel")} *
                    </Typography>
                    <Select
                      placeholder={t(
                        "editUser.workPermitVisibilityPlaceholder"
                      )}
                      options={visibilityTypes}
                      value={userData.work_permit_visibility}
                      onChange={handleSingleSelect("work_permit_visibility")}
                      onBlur={() => setVisibilitySettingsFocused(true)}
                      styles={{
                        ...singleSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      aria-label={t("editUser.workPermitVisibilityAria")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {t("editUser.observationVisibilityLabel")} *
                    </Typography>
                    <Select
                      placeholder={t(
                        "editUser.observationVisibilityPlaceholder"
                      )}
                      options={visibilityTypes}
                      value={userData.observation_visibility}
                      onChange={handleSingleSelect("observation_visibility")}
                      onBlur={() => setVisibilitySettingsFocused(true)}
                      styles={{
                        ...singleSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      aria-label={t("editUser.observationVisibilityAria")}
                    />
                  </Grid>
                </Grid>
                {visibilitySettingsFocused &&
                  (!userData.work_permit_visibility ||
                    !userData.observation_visibility) && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 2, display: "block" }}
                    >
                      {t("editUser.visibilityError")}
                    </Typography>
                  )}
              </Paper>
            </Stack>
          </form>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("editUser.cancelButton")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!formValid || editIsLoading}
          variant="contained"
          color="primary"
          size="large"
          startIcon={
            editIsLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <EditIcon />
            )
          }
          aria-label={t("editUser.saveButtonAria")}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: theme.shadows[2],
            "&:hover": {
              boxShadow: theme.shadows[4],
              transform: "translateY(-1px)",
            },
            "&:disabled": {
              bgcolor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
            transition: "all 0.2s ease",
          }}
        >
          {editIsLoading ? t("editUser.savingText") : t("editUser.saveButton")}
        </Button>
      </DialogActions>

      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </Dialog>
  );
};

EditUser.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userDetails: PropTypes.object,
  setEditingUser: PropTypes.func.isRequired,
};

export default EditUser;
